import React from "react";
import BiogasDataGrid from "../../grids/biogas/BiogasDataGrid";
import { useCreateBiogasData, useDeleteBiogasData, useUpdateEpdGeneralInformation } from "services/api/mutations";
import { useBiogas, useEPDDefinitionInformation } from "services/api/queries";
import { YES_NO, YesNoDataSource } from "../../constants";
import CompilerSelectControlledComponent from "components/v2/epd-compiler-inputs/CompilerSelectControlledComponent";
import { BiogasDataModel } from "types/types";

interface BiogasDataProps {
  epdId: string;
  epdVersionId: string;
  errors: any;
}

const BiogasDataPanel: React.FC<BiogasDataProps> = ({ epdId, epdVersionId, errors }: BiogasDataProps) => {

  const biogasData = useBiogas(epdVersionId).data;
  const createBiogasMutation = useCreateBiogasData(epdVersionId);
  const deleteBiogasMutation = useDeleteBiogasData(epdVersionId);

  const epdDefinitionInformation = useEPDDefinitionInformation(epdVersionId).data;
  const updateMutation = useUpdateEpdGeneralInformation(epdVersionId);

  const onChangeBiogasApplicable = (propertyName: string, val: string) => {
    if (!epdId) return;

    if (val === YES_NO.YES && biogasData?.length === 0) {
      // Create new biogas data if it doesn't exist.
      const newBiogasData: BiogasDataModel = {
        epdVersionId: epdVersionId,
        usedInProcessName: 'Unnamed process',
      };
      createBiogasMutation.mutate(newBiogasData);
    } else if (val === YES_NO.NO && biogasData && biogasData.length > 0) {
      // Delete all biogas data to avoid problems during PDF generation.
      const ids = biogasData.map(x => x.id).filter((id): id is string => typeof id === 'string');
      if (ids.length > 0) {
        deleteBiogasMutation.mutate(ids);
      }
    }

    updateMutation.mutate({
      epdId,
      versionId: epdVersionId,
      propertyName,
      newValue: val
    });
  };

  return (
    <>
      <CompilerSelectControlledComponent
        label="Is biogas modelling applicable?"
        name="biogasApplicable"
        options={YesNoDataSource}
        value={YesNoDataSource.find((item) => item.value === epdDefinitionInformation?.biogasApplicable) || null}
        onChanged={onChangeBiogasApplicable}
        required
        placeholder="Select"
        isClearable={false}
        error={errors.biogasApplicable}
      />
      {epdDefinitionInformation?.biogasApplicable === YES_NO.YES && (
        <>
          {Array.isArray(biogasData) && biogasData.length > 0 ? (
            biogasData.map((item, idx) => (
              <BiogasDataGrid
                key={item.id || idx}
                epdVersionId={epdVersionId}
                errors={errors}
                biogasData={item}
              />
            ))
          ) : null}
        </>
      )}
    </>
  );
};

export default BiogasDataPanel;